import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { 
  Star, CheckCircle, Send, Loader2, MessageSquare, ThumbsUp, ExternalLink
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function EncuestaPublicaPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [encuestaData, setEncuestaData] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [comentarioGeneral, setComentarioGeneral] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const preguntaRefs = useRef([]);

  useEffect(() => {
    fetchEncuesta();
  }, [token]);

  useEffect(() => {
    if (!encuestaData || submitted) return;
    const onScroll = () => {
      const objetivo = window.innerHeight * 0.45;
      let mejor = -1;
      let mejorDist = Infinity;
      preguntaRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - objetivo);
        if (dist < mejorDist) {
          mejorDist = dist;
          mejor = i;
        }
      });
      if (mejor >= 0) {
        setCurrentStep((prev) => (prev === mejor ? prev : mejor));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [encuestaData, submitted]);

  const fetchEncuesta = async () => {
    try {
      // Si es modo demo, usar endpoint de preview
      if (token === 'demo') {
        const res = await axios.get(`${API}/encuestas/preview-portal`);
        setEncuestaData(res.data);
      } else {
        const res = await axios.get(`${API}/encuestas/responder/${token}`);
        if (res.data.ya_respondido) {
          setSubmitted(true);
          setEncuestaData(res.data);
        } else {
          setEncuestaData(res.data);
        }
      }
    } catch (err) {
      toast.error("Encuesta no encontrada o ya expirada");
    } finally {
      setLoading(false);
    }
  };

  const isPreviewMode = encuestaData?.preview_mode === true;

  const handleSubmit = async () => {
    // En modo preview, mostrar mensaje y no enviar
    if (isPreviewMode) {
      toast.info("Esto es una vista previa. Las respuestas no se guardarán.");
      setSubmitted(true);
      return;
    }

    // Validar respuestas obligatorias
    const preguntasObligatorias = encuestaData.encuesta.preguntas.filter(p => p.obligatoria);
    const sinResponder = preguntasObligatorias.filter(p => {
      const resp = respuestas[p.id];
      // Para selección múltiple, verificar que el array tenga al menos un elemento
      if (Array.isArray(resp)) {
        return resp.length === 0;
      }
      return !resp;
    });
    
    if (sinResponder.length > 0) {
      toast.error("Por favor responda todas las preguntas obligatorias");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/encuestas/responder/${token}`, {
        respuestas,
        comentario_general: comentarioGeneral
      });
      setSubmitted(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar respuesta");
    } finally {
      setSubmitting(false);
    }
  };

  const renderPregunta = (pregunta, index) => {
    const isActive = currentStep === index;
    
    return (
      <div 
        key={pregunta.id} 
        ref={(el) => { preguntaRefs.current[index] = el; }}
        className={`transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-50'}`}
      >
        <Card className={`mb-4 ${isActive ? 'ring-2 ring-primary' : ''}`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <CardTitle className="text-lg">
                {index + 1}. {pregunta.pregunta}
                {pregunta.obligatoria && <span className="text-red-500 ml-1">*</span>}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {pregunta.tipo === "nps" && renderNPS(pregunta)}
            {pregunta.tipo === "satisfaccion" && renderSatisfaccion(pregunta)}
            {pregunta.tipo === "texto" && renderTexto(pregunta)}
            {pregunta.tipo === "opcion_multiple" && renderOpcionMultiple(pregunta)}
            {pregunta.tipo === "seleccion_multiple" && renderSeleccionMultiple(pregunta)}
            {pregunta.tipo === "escala" && renderEscala(pregunta)}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderNPS = (pregunta) => {
    const valores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const selected = respuestas[pregunta.id];
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Nada probable</span>
          <span>Muy probable</span>
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {valores.map((val) => (
            <button
              key={val}
              onClick={() => {
                setRespuestas({ ...respuestas, [pregunta.id]: val });
                if (currentStep < encuestaData.encuesta.preguntas.length - 1) {
                  setTimeout(() => setCurrentStep(currentStep + 1), 300);
                }
              }}
              className={`w-12 h-12 rounded-lg border-2 font-bold transition-all ${
                selected === val
                  ? val >= 9 ? 'bg-green-500 border-green-500 text-white' :
                    val >= 7 ? 'bg-yellow-500 border-yellow-500 text-white' :
                    'bg-red-500 border-red-500 text-white'
                  : 'border-gray-300 hover:border-primary'
              }`}
            >
              {val}
            </button>
          ))}
        </div>
{/* Etiquetas NPS removidas - son para análisis interno */}
      </div>
    );
  };

  const renderSatisfaccion = (pregunta) => {
    const selected = respuestas[pregunta.id];
    
    return (
      <div className="flex justify-center gap-2">
        {[1, 2, 3, 4, 5].map((val) => (
          <button
            key={val}
            onClick={() => {
              setRespuestas({ ...respuestas, [pregunta.id]: val });
              if (currentStep < encuestaData.encuesta.preguntas.length - 1) {
                setTimeout(() => setCurrentStep(currentStep + 1), 300);
              }
            }}
            className="p-2 transition-transform hover:scale-110"
          >
            <Star
              className={`w-10 h-10 transition-colors ${
                selected >= val
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-gray-300 hover:text-yellow-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const renderTexto = (pregunta) => {
    return (
      <Textarea
        value={respuestas[pregunta.id] || ""}
        onChange={(e) => setRespuestas({ ...respuestas, [pregunta.id]: e.target.value })}
        placeholder="Escriba su respuesta aquí..."
        rows={3}
        className="w-full"
      />
    );
  };

  const renderOpcionMultiple = (pregunta) => {
    return (
      <RadioGroup
        value={respuestas[pregunta.id] || ""}
        onValueChange={(val) => {
          setRespuestas({ ...respuestas, [pregunta.id]: val });
          if (currentStep < encuestaData.encuesta.preguntas.length - 1) {
            setTimeout(() => setCurrentStep(currentStep + 1), 300);
          }
        }}
      >
        <div className="space-y-2">
          {pregunta.opciones?.map((opcion, idx) => (
            <div key={idx} className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted cursor-pointer">
              <RadioGroupItem value={opcion} id={`${pregunta.id}-${idx}`} />
              <Label htmlFor={`${pregunta.id}-${idx}`} className="flex-1 cursor-pointer">
                {opcion}
              </Label>
            </div>
          ))}
        </div>
      </RadioGroup>
    );
  };

  const renderSeleccionMultiple = (pregunta) => {
    // Para selección múltiple, guardamos un array de opciones seleccionadas
    const seleccionadas = respuestas[pregunta.id] || [];
    
    const toggleOpcion = (opcion) => {
      let nuevasSelecciones;
      if (seleccionadas.includes(opcion)) {
        nuevasSelecciones = seleccionadas.filter(o => o !== opcion);
      } else {
        nuevasSelecciones = [...seleccionadas, opcion];
      }
      setRespuestas({ ...respuestas, [pregunta.id]: nuevasSelecciones });
    };
    
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground mb-3">Puede seleccionar más de una opción</p>
        {pregunta.opciones?.map((opcion, idx) => (
          <div 
            key={idx} 
            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              seleccionadas.includes(opcion) 
                ? 'bg-primary/10 border-primary' 
                : 'hover:bg-muted'
            }`}
            onClick={() => toggleOpcion(opcion)}
          >
            <Checkbox 
              checked={seleccionadas.includes(opcion)}
              onCheckedChange={() => toggleOpcion(opcion)}
              id={`${pregunta.id}-${idx}`}
            />
            <Label htmlFor={`${pregunta.id}-${idx}`} className="flex-1 cursor-pointer">
              {opcion}
            </Label>
          </div>
        ))}
      </div>
    );
  };

  const renderEscala = (pregunta) => {
    const min = pregunta.escala_min || 1;
    const max = pregunta.escala_max || 10;
    const valores = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    const selected = respuestas[pregunta.id];
    
    return (
      <div className="flex flex-wrap gap-2 justify-center">
        {valores.map((val) => (
          <button
            key={val}
            onClick={() => {
              setRespuestas({ ...respuestas, [pregunta.id]: val });
              if (currentStep < encuestaData.encuesta.preguntas.length - 1) {
                setTimeout(() => setCurrentStep(currentStep + 1), 300);
              }
            }}
            className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${
              selected === val
                ? 'bg-primary border-primary text-white'
                : 'border-gray-300 hover:border-primary'
            }`}
          >
            {val}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!encuestaData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-xl font-bold mb-2">Encuesta no encontrada</h2>
            <p className="text-muted-foreground">
              El enlace de la encuesta no es válido o ha expirado.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    // Configuración de despedida personalizable
    const despedida = {
      titulo: encuestaData.encuesta?.despedida_titulo || "¡Gracias!",
      mensaje: encuestaData.encuesta?.despedida_mensaje || encuestaData.encuesta?.mensaje_agradecimiento || "Sus comentarios son muy valiosos para nosotros.",
      mensajeSecundario: encuestaData.encuesta?.despedida_mensaje_secundario || "Su respuesta ha sido registrada exitosamente",
      colorFondoInicio: encuestaData.encuesta?.despedida_color_fondo_inicio || "#ecfdf5",
      colorFondoFin: encuestaData.encuesta?.despedida_color_fondo_fin || "#d1fae5",
      colorTitulo: encuestaData.encuesta?.despedida_color_titulo || "#166534",
      colorIcono: encuestaData.encuesta?.despedida_color_icono || "#16a34a",
      colorIconoFondo: encuestaData.encuesta?.despedida_color_icono_fondo || "#dcfce7",
      colorMensajeFondo: encuestaData.encuesta?.despedida_color_mensaje_fondo || "#f0fdf4",
      colorMensajeTexto: encuestaData.encuesta?.despedida_color_mensaje_texto || "#15803d",
      mostrarLogo: encuestaData.encuesta?.despedida_mostrar_logo || false,
      mostrarIconoCheck: encuestaData.encuesta?.despedida_mostrar_icono_check !== false,
      mostrarIconoPulgar: encuestaData.encuesta?.despedida_mostrar_icono_pulgar !== false,
      tamañoTitulo: encuestaData.encuesta?.despedida_tamaño_titulo || "2xl",
      tamañoMensaje: encuestaData.encuesta?.despedida_tamaño_mensaje || "base",
      fuenteTitulo: encuestaData.encuesta?.despedida_fuente_titulo || "default",
      urlRedireccion: encuestaData.encuesta?.despedida_url_redireccion || "",
      textoBotonRedireccion: encuestaData.encuesta?.despedida_texto_boton_redireccion || "Visitar sitio web",
      mostrarBotonRedireccion: encuestaData.encuesta?.despedida_mostrar_boton_redireccion || false,
      logoUrl: encuestaData.encuesta?.logo_url || encuestaData.sistema?.logo_url,
      logoPosicion: encuestaData.encuesta?.despedida_logo_posicion || "arriba",
      logoTamaño: encuestaData.encuesta?.despedida_logo_tamaño || "md"
    };

    const getFuenteClass = () => {
      switch(despedida.fuenteTitulo) {
        case 'serif': return 'font-serif';
        case 'mono': return 'font-mono';
        default: return '';
      }
    };

    const getLogoHeight = () => {
      switch(despedida.logoTamaño) {
        case 'sm': return '40px';
        case 'md': return '60px';
        case 'lg': return '80px';
        case 'xl': return '100px';
        default: return '60px';
      }
    };

    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(to bottom right, ${despedida.colorFondoInicio}, ${despedida.colorFondoFin})`
        }}
      >
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {/* Logo (posición arriba) */}
            {despedida.mostrarLogo && despedida.logoUrl && despedida.logoPosicion === "arriba" && (
              <div className="mb-4">
                <img 
                  src={despedida.logoUrl} 
                  alt="Logo" 
                  className="mx-auto object-contain"
                  style={{ height: getLogoHeight() }}
                />
              </div>
            )}
            
            {/* Ícono de Check */}
            {despedida.mostrarIconoCheck && (
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: despedida.colorIconoFondo }}
              >
                <CheckCircle 
                  className="w-12 h-12" 
                  style={{ color: despedida.colorIcono }}
                />
              </div>
            )}
            
            {/* Título */}
            <h2 
              className={`font-bold mb-2 text-${despedida.tamañoTitulo} ${getFuenteClass()}`}
              style={{ color: despedida.colorTitulo }}
            >
              {despedida.titulo}
            </h2>
            
            {/* Mensaje Principal */}
            <p className={`text-muted-foreground text-${despedida.tamañoMensaje}`}>
              {despedida.mensaje}
            </p>
            
            {/* Mensaje Secundario con ícono de pulgar */}
            {despedida.mostrarIconoPulgar && (
              <div 
                className="mt-6 p-4 rounded-lg"
                style={{ backgroundColor: despedida.colorMensajeFondo }}
              >
                <ThumbsUp 
                  className="w-8 h-8 mx-auto mb-2" 
                  style={{ color: despedida.colorIcono }}
                />
                <p 
                  className="text-sm"
                  style={{ color: despedida.colorMensajeTexto }}
                >
                  {despedida.mensajeSecundario}
                </p>
              </div>
            )}
            
            {/* Logo (posición abajo) */}
            {despedida.mostrarLogo && despedida.logoUrl && despedida.logoPosicion === "abajo" && (
              <div className="mt-6 pt-4 border-t">
                <img 
                  src={despedida.logoUrl} 
                  alt="Logo" 
                  className="mx-auto object-contain"
                  style={{ height: getLogoHeight() }}
                />
              </div>
            )}
            
            {/* Botón de redirección */}
            {despedida.mostrarBotonRedireccion && despedida.urlRedireccion && (
              <div className="mt-6">
                <a 
                  href={despedida.urlRedireccion}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: despedida.colorIcono }}
                >
                  {despedida.textoBotonRedireccion}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const preguntas = encuestaData.encuesta.preguntas || [];
  const preguntasRespondidas = Object.keys(respuestas).length;
  const progreso = preguntas.length > 0 ? (preguntasRespondidas / preguntas.length) * 100 : 0;
  
  // Colores y configuración personalizados
  const colorPrimario = encuestaData.encuesta.color_primario || "#0C314C";
  const colorSecundario = encuestaData.encuesta.color_secundario || "#45E69A";
  const colorFondo = encuestaData.encuesta.color_fondo || "#f8fafc";
  const logoUrl = encuestaData.encuesta.logo_url || (encuestaData.encuesta.mostrar_logo_empresa !== false ? encuestaData.sistema?.logo_url : null);
  const usarImagenBienvenida = encuestaData.encuesta.usar_imagen_bienvenida && encuestaData.encuesta.imagen_bienvenida_url;
  const usarImagenFondo = encuestaData.encuesta.usar_imagen_fondo && encuestaData.encuesta.imagen_fondo_url;
  const mostrarProgreso = encuestaData.encuesta.mostrar_progreso !== false;
  const textoFooter = encuestaData.encuesta.texto_footer || "";
  const estiloLogo = encuestaData.encuesta.estilo_logo || "sin_fondo";
  const tamañoLogo = encuestaData.encuesta.tamaño_logo || "300";
  const tamañoImagenBienvenida = encuestaData.encuesta.tamaño_imagen_bienvenida || "100";
  const mostrarLogo = encuestaData.encuesta.mostrar_logo_empresa !== false;
  const campoNombre = encuestaData.encuesta.campo_nombre_mostrar || "nombre_cliente";
  
  // Obtener el nombre según la configuración
  const getNombreCliente = () => {
    if (campoNombre === "ninguno") return null;
    const dest = encuestaData.destinatario || {};
    switch(campoNombre) {
      case "contacto": return dest.contacto || dest.nombre_cliente || dest.nombre;
      case "empresa": return dest.empresa || dest.nombre_cliente || dest.nombre;
      default: return dest.nombre_cliente || dest.nombre;
    }
  };
  const nombreMostrar = getNombreCliente();
  
  // Tamaño del logo según configuración (porcentaje de 80px base)
  const getLogoSize = () => {
    const baseSize = 80;
    const percentage = parseInt(tamañoLogo) || 100;
    const size = Math.round(baseSize * (percentage / 100));
    return size;
  };
  const logoSizePx = getLogoSize();
  const logoClasses = estiloLogo === "circular" ? "rounded-full bg-white shadow-lg" : estiloLogo === "sin_fondo" ? "bg-transparent" : "rounded-xl bg-white shadow-lg";

  // Opciones avanzadas de espaciado
  const espacioLogoTitulo = encuestaData.encuesta.espacio_logo_titulo || "8";
  const espacioTituloSubtitulo = encuestaData.encuesta.espacio_titulo_subtitulo || "4";
  const espacioHeaderContenido = encuestaData.encuesta.espacio_header_contenido || "24";
  const paddingHeader = encuestaData.encuesta.padding_header || "24";

  return (
    <div 
      className="min-h-screen py-8 px-4 relative"
      style={{ 
        backgroundColor: colorFondo,
        backgroundImage: usarImagenFondo ? `url(${encuestaData.encuesta.imagen_fondo_url})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay si hay imagen de fondo */}
      {usarImagenFondo && <div className="absolute inset-0 bg-black/10" />}
      
      {/* Banner de modo preview */}
      {isPreviewMode && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 z-50">
          <strong>MODO VISTA PREVIA</strong> - Esta es una demostración. Las respuestas no se guardarán.
        </div>
      )}
      
      <div className={`max-w-2xl mx-auto relative z-10 ${isPreviewMode ? 'mt-10' : ''}`}>
        {/* Header con colores personalizados */}
        <div 
          className="text-center rounded-xl"
          style={{ 
            background: `linear-gradient(135deg, ${colorPrimario} 0%, ${colorPrimario}dd 100%)`,
            padding: `${paddingHeader}px`,
            marginBottom: `${espacioHeaderContenido}px`
          }}
        >
          {mostrarLogo && logoUrl && (
            <div 
              className={`mx-auto p-2 flex items-center justify-center ${logoClasses}`}
              style={{ 
                width: `${logoSizePx}px`, 
                height: `${logoSizePx}px`,
                marginBottom: `${espacioLogoTitulo}px`
              }}
            >
              <img 
                src={logoUrl} 
                alt="Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          <h1 
            className="text-2xl font-bold"
            style={{ 
              color: encuestaData.encuesta.color_texto_encabezado || '#FFFFFF',
              marginBottom: `${espacioTituloSubtitulo}px`
            }}
          >
            {encuestaData.encuesta.nombre}
          </h1>
          {nombreMostrar && String(nombreMostrar).trim() !== "" && (
            <p style={{ color: encuestaData.encuesta.color_texto_encabezado || '#FFFFFF', opacity: 0.9 }}>
              Hola, <strong style={{ color: encuestaData.encuesta.color_texto_encabezado || '#FFFFFF' }}>{nombreMostrar}</strong>
            </p>
          )}
        </div>

        {/* Mensaje de bienvenida o Imagen de bienvenida */}
        {usarImagenBienvenida ? (
          <div 
            className="mb-6 rounded-xl overflow-hidden shadow-md mx-auto"
            style={{ width: `${tamañoImagenBienvenida}%`, maxWidth: '100%' }}
          >
            <img 
              src={encuestaData.encuesta.imagen_bienvenida_url} 
              alt="Bienvenida"
              className="w-full h-auto object-cover"
            />
          </div>
        ) : (
          <Card className="mb-6" style={{ borderColor: `${colorPrimario}30`, backgroundColor: `${colorPrimario}08` }}>
            <CardContent className="pt-4">
              <p className="text-center text-slate-700">
                {encuestaData.encuesta.mensaje_bienvenida}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Progreso - condicional según configuración */}
        {mostrarProgreso && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progreso</span>
              <span className="font-medium">{preguntasRespondidas} de {preguntas.length}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full transition-all duration-300"
                style={{ width: `${progreso}%`, backgroundColor: colorSecundario }}
              />
            </div>
          </div>
        )}

        {/* Preguntas */}
        <div className="space-y-4">
          {preguntas.map((pregunta, index) => renderPregunta(pregunta, index))}
        </div>

        {/* Comentario general */}
        <Card className="mt-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comentarios adicionales
            </CardTitle>
            <CardDescription>
              ¿Hay algo más que quiera compartir con nosotros? (Opcional)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={comentarioGeneral}
              onChange={(e) => setComentarioGeneral(e.target.value)}
              placeholder="Escriba sus comentarios aquí..."
              rows={4}
              className="w-full"
            />
          </CardContent>
        </Card>

        {/* Botón enviar */}
        <div className="mt-8 flex justify-center">
          <button 
            onClick={handleSubmit} 
            disabled={submitting}
            className="px-12 py-3 rounded-full font-bold text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            style={{ backgroundColor: colorSecundario, color: colorPrimario }}
            data-testid="enviar-encuesta-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Enviando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Enviar Respuesta
              </span>
            )}
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          © {new Date().getFullYear()} {encuestaData.sistema?.nombre || 'QAsesores'}. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
